import React from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
  Table,
  Input,
  Row,
  Col,
  Badge,
} from 'reactstrap';
import axios from 'axios';
import { Link } from 'react-router-dom';

import isAuthenticated from '../Auth/isAuthenticated';
import * as userUrls from '../urls/api-users/admin';
import PaginationComponent from '../components/SharedComponents/PaginationComponent';

class ReferralPartners extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      validToken: false,
      partners: [],
      currentPage: 1,
      totalPages: 1,
      totalCount: 0,
      pageSize: 25,
      modeFilter: '',
      searchQuery: '',
      isLoading: false,
      error: null,
    };
  }

  normalizePartnerRow = partner => {
    const row = partner && typeof partner === 'object' ? partner : {};

    return {
      driver_auth0_id: row.driver_auth0_id || '',
      driver_id: row.driver_id || '',
      user_id: row.user_id || '',
      name: row.name || 'N/A',
      email: row.email || 'N/A',
      phone: row.phone || 'N/A',
      active_qr_mode: row.active_qr_mode || 'referral_partner',
      referral_partner: row.referral_partner || null,
      affiliate_partner: row.affiliate_partner || null,
    };
  };

  componentDidMount() {
    if (!isAuthenticated()) {
      this.props.history.push('/');
      return;
    }

    if (this.props.role === 0 || this.props.role === 100) {
      this.setState({ validToken: true }, () => {
        this.fetchReferralPartners();
      });
      return;
    }

    this.setState({ validToken: false });
    this.props.history.push('/');
  }

  fetchReferralPartners = (page = this.state.currentPage, overrides = {}) => {
    const modeFilter = Object.prototype.hasOwnProperty.call(overrides, 'modeFilter') ? overrides.modeFilter : this.state.modeFilter;
    const searchQuery = Object.prototype.hasOwnProperty.call(overrides, 'searchQuery') ? overrides.searchQuery : this.state.searchQuery;
    const { pageSize } = this.state;

    this.setState({ isLoading: true, error: null });

    axios({
      method: 'get',
      url: userUrls.referralPartnersList,
      params: {
        page,
        page_size: pageSize,
        mode: modeFilter || undefined,
        search: searchQuery || undefined,
      },
      withCredentials: true,
      headers: {
        'content-type': 'application/json',
        Authorization: 'Bearer ' + localStorage.getItem('access_token'),
      },
    })
      .then(response => {
        const payload = response.data && response.data.payload ? response.data.payload : {};
        const results = Array.isArray(payload.results) ? payload.results.map(this.normalizePartnerRow) : [];
        const total = Number.isFinite(payload.total) ? payload.total : 0;
        const totalPages = Math.max(Math.ceil(total / pageSize), 1);

        this.setState({
          partners: results,
          currentPage: payload.page || page,
          totalPages,
          totalCount: total,
          modeFilter,
          searchQuery,
          isLoading: false,
        });
      })
      .catch(error => {
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          this.props.history.push('/logout');
          return;
        }

        this.setState({
          isLoading: false,
          error: 'Unable to load referral partners.',
        });
      });
  };

  handleModeFilter = modeFilter => {
    this.fetchReferralPartners(1, { modeFilter });
  };

  handleSearchChange = event => {
    const searchQuery = event.target.value;
    this.setState({ searchQuery }, () => {
      this.fetchReferralPartners(1, { searchQuery });
    });
  };

  handlePageChange = page => {
    this.fetchReferralPartners(page);
  };

  renderModeBadge = mode => {
    if (mode === 'sales_affiliate') {
      return <Badge color="warning">Sales Affiliate</Badge>;
    }

    return <Badge color="info">Referral Partner</Badge>;
  };

  render() {
    return (
      this.state.validToken && (
        <div className="content">
          <Row>
            <Col md="12">
              <Card>
                <CardHeader>
                  <div className="tools float-right">
                    <Row>
                      <Col>
                        <UncontrolledDropdown style={{ marginRight: 20, marginTop: 5 }}>
                          <DropdownToggle caret className="btn-link btn-icon" color="default" data-toggle="dropdown" type="button">
                            <i className="fas fa-filter"></i>
                          </DropdownToggle>
                          <DropdownMenu right>
                            <DropdownItem className={!this.state.modeFilter ? 'text-success' : ''} onClick={() => this.handleModeFilter('')}>
                              All modes
                            </DropdownItem>
                            <DropdownItem className={this.state.modeFilter === 'referral_partner' ? 'text-success' : ''} onClick={() => this.handleModeFilter('referral_partner')}>
                              Referral Partner
                            </DropdownItem>
                            <DropdownItem className={this.state.modeFilter === 'sales_affiliate' ? 'text-success' : ''} onClick={() => this.handleModeFilter('sales_affiliate')}>
                              Sales Affiliate
                            </DropdownItem>
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      </Col>
                      <Col>
                        <Input placeholder="Search drivers..." value={this.state.searchQuery} onChange={this.handleSearchChange} />
                      </Col>
                    </Row>
                  </div>
                  <CardTitle tag="h3">
                    <i className="tim-icons text-primary" /> Referral Partners
                  </CardTitle>
                  <div style={{ color: '#9a9a9a', marginTop: '8px' }}>{this.state.totalCount} drivers</div>
                </CardHeader>
                <CardBody>
                  {this.state.error ? <div style={{ color: '#ff6b6b', marginBottom: '16px' }}>{this.state.error}</div> : null}
                  {this.state.isLoading ? <div style={{ marginBottom: '16px' }}>Loading referral partners...</div> : null}
                  <Table responsive>
                    <thead className="text-primary">
                      <tr>
                        <th>Driver Auth0 ID</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Active QR Mode</th>
                        <th>Referral Code</th>
                        <th>Affiliate Code</th>
                        <th>Affiliate Rate</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {this.state.partners.map((partner, index) => (
                        <tr key={partner.driver_auth0_id || partner.driver_id || partner.user_id || `partner-row-${index}`}>
                          <td>{partner.driver_auth0_id}</td>
                          <td>{partner.name || 'N/A'}</td>
                          <td>{partner.email || 'N/A'}</td>
                          <td>{partner.phone || 'N/A'}</td>
                          <td>{this.renderModeBadge(partner.active_qr_mode)}</td>
                          <td>{partner.referral_partner ? partner.referral_partner.referral_code : 'N/A'}</td>
                          <td>{partner.affiliate_partner ? partner.affiliate_partner.referral_code : 'N/A'}</td>
                          <td>{partner.affiliate_partner ? `${(Number(partner.affiliate_partner.commission_rate || 0) * 100).toFixed(2)}%` : 'N/A'}</td>
                          <td>
                            {partner.driver_auth0_id ? (
                              <Link to={'/admin/users/' + encodeURIComponent(partner.driver_auth0_id)}>
                                <i className="tim-icons icon-badge" style={{ fontSize: '1.5em', cursor: 'pointer', color: '#cdcaca' }} />
                              </Link>
                            ) : (
                              <span style={{ color: '#6c757d' }}>Unavailable</span>
                            )}
                          </td>
                        </tr>
                      ))}
                      {!this.state.isLoading && this.state.partners.length === 0 ? (
                        <tr>
                          <td colSpan="9" style={{ textAlign: 'center', color: '#9a9a9a' }}>
                            No referral partners found.
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </Table>
                  <PaginationComponent currentPage={this.state.currentPage} totalPages={this.state.totalPages} onPageChange={this.handlePageChange} />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      )
    );
  }
}

export default ReferralPartners;